import { useEffect, useState } from "react";
import { Check, X, Search, Plus, BookCheck } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import { eur, fmtDate } from "../../lib/format";
import api from "../../lib/api";
import type { BankTransaction, MatchSuggestion, MatchTargetType } from "../../types/bank";

interface MatchSuggestionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  tx: BankTransaction | null;
  onDone: () => void;
}

const targetLabels: Record<string, string> = {
  INVOICE: "Factuur",
  EXPENSE: "Uitgave",
  CREATE_PAYMENT: "Nieuwe inkomende betaling (los)",
  CREATE_EXPENSE: "Nieuwe uitgave (bon ontbreekt)",
  BOOK_INTERNAL: "Geboekt zonder koppeling",
};

/** Gestandaardiseerde redenen voor boeken zonder bon/factuur — onderbouwing
 *  richting de Belastingdienst. */
const internalReasons = [
  { value: "Eigen bankrekening — overboeking tussen eigen rekeningen", label: "Eigen bankrekening overgemaakt (overboeking eigen rekeningen)" },
  { value: "Bankkosten / rente", label: "Bankkosten / rente" },
  { value: "Kasstorting of kasopname", label: "Kasstorting of kasopname" },
  { value: "Belastingdienst — btw-aangifte betaling", label: "Belastingdienst — btw-betaling" },
  { value: "Belastingdienst — inkomstenbelasting / vpb", label: "Belastingdienst — inkomstenbelasting / vpb" },
  { value: "Salaris / loonheffing", label: "Salaris / loonheffing" },
  { value: "Privéopname of privé-storting op zakelijke rekening", label: "Privéopname / privé-storting" },
  { value: "Terugstorting / correctie (bijv. dubbelle overboeking)", label: "Terugstorting / correctie" },
  { value: "Overig — zie opmerking", label: "Overig (omschrijf in de opmerking)" },
];

/** Modal met match-suggesties voor één transactie + handmatig koppelen. */
export function MatchSuggestionPanel({ isOpen, onClose, tx, onDone }: MatchSuggestionPanelProps) {
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [manualType, setManualType] = useState("INVOICE");
  const [manualQuery, setManualQuery] = useState("");
  const [manualResults, setManualResults] = useState<{ id: string; label: string }[]>([]);
  // Boeken zonder koppeling: reden (dropdown) + vrije opmerking.
  const [internalReason, setInternalReason] = useState("");
  const [internalNote, setInternalNote] = useState("");

  useEffect(() => {
    if (!isOpen || !tx) return;
    setError("");
    setSuggestions([]);
    setInternalReason("");
    setInternalNote("");
    setLoading(true);
    api
      .get(`/api/bank-transactions/${tx.id}/suggestions`)
      .then((res) => setSuggestions(res.data.suggestions ?? []))
      .catch(() => setError("Suggesties ophalen mislukt."))
      .finally(() => setLoading(false));
  }, [isOpen, tx]);

  const apply = async (targetType: MatchTargetType, targetId?: string, extra?: { reason?: string; note?: string }) => {
    if (!tx) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/bank-transactions/${tx.id}/match`, { targetType, targetId, ...extra });
      onClose();
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Koppelen mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const searchManual = async () => {
    if (!manualQuery.trim()) return;
    try {
      if (manualType === "INVOICE") {
        const { data } = await api.get("/api/invoices", { params: { q: manualQuery } });
        setManualResults((data ?? []).slice(0, 10).map((i: any) => ({ id: i.id, label: `${i.number} · ${i.contact?.companyName ?? ""} · ${eur(Number(i.totalEur ?? i.total))}` })));
      } else {
        const { data } = await api.get("/api/expenses", { params: { q: manualQuery } });
        setManualResults((data ?? []).slice(0, 10).map((e: any) => ({ id: e.id, label: `${e.description} · ${e.merchant ?? ""} · ${eur(Number(e.amountEurInclVat))}` })));
      }
    } catch {
      setManualResults([]);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transactie koppelen" subtitle={tx ? `${fmtDate(tx.date)} · ${tx.counterparty || "—"} · ${eur(Number(tx.amountEur))}` : ""} size="md">
      <div className="space-y-4">
        {loading && <p className="text-sm text-slate-500">Suggesties berekenen…</p>}

        {!loading && suggestions.length === 0 && !error && (
          <p className="text-sm text-slate-500">Geen concrete suggesties gevonden. Koppel handmatig of boek een nieuwe post (onderaan).</p>
        )}

        <div className="space-y-2">
          {suggestions.map((s, i) => (
            <div key={`${s.targetType}-${s.targetId ?? i}`} className="border border-slate-200 rounded-xl p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 text-sm">
                  {targetLabels[s.targetType] ?? s.targetType}
                  {s.invoice ? ` ${s.invoice.number} — ${s.invoice.contactName ?? ""} (${eur(s.invoice.openEur)} open)` : ""}
                  {s.expense ? ` ${s.expense.description}${s.expense.merchant ? ` — ${s.expense.merchant}` : ""}` : ""}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{s.reasons.join(" · ") || "Geen concrete redenen"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-sm font-bold ${s.score >= 85 ? "text-emerald-600" : s.score >= 40 ? "text-amber-600" : "text-slate-400"}`}>{s.score}</span>
                {s.targetId ? (
                  <Button onClick={() => apply(s.targetType, s.targetId)} loading={busy} className="!px-3 !py-1.5">
                    <Check size={16} /> Koppel
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={() => apply(s.targetType)} loading={busy} className="!px-3 !py-1.5">
                    <Plus size={16} /> Boek
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 pt-4 space-y-3">
          <p className="text-sm font-semibold text-slate-700">Handmatig koppelen</p>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Soort"
              options={[
                { value: "INVOICE", label: "Factuur" },
                { value: "EXPENSE", label: "Uitgave" },
              ]}
              value={manualType}
              onChange={(e) => { setManualType(e.target.value); setManualResults([]); }}
            />
            <Input label="Zoekterm (nummer/naam)" value={manualQuery} onChange={(e) => setManualQuery(e.target.value)} />
          </div>
          <Button variant="ghost" onClick={searchManual}>
            <Search size={18} /> Zoeken
          </Button>
          {manualResults.length > 0 && (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl max-h-48 overflow-y-auto">
              {manualResults.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => apply(manualType as MatchTargetType, r.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between"
                  >
                    <span className="truncate">{r.label}</span>
                    <Check size={16} className="text-emerald-600 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-200 pt-4 space-y-3">
          <p className="text-sm font-semibold text-slate-700">Boeken zonder koppeling</p>
          <p className="text-xs text-slate-500 -mt-2">
            Geen bon of factuur nodig — bijv. overboeking tussen eigen rekeningen, bankkosten of een betaling aan de Belastingdienst.
            Kies de reden en vul zo nodig een opmerking in (deze komt bij de controle en de maandafsluiting te staan).
          </p>
          <Select
            label="Reden"
            options={[
              { value: "", label: "Kies een reden..." },
              ...internalReasons,
            ]}
            value={internalReason}
            onChange={(e) => setInternalReason(e.target.value)}
          />
          <Input
            label="Opmerking / omschrijving (optioneel)"
            placeholder="Bijv. 'Overboeking van rekening NL..1234 naar zakelijke rekening' of 'Definitieve aanslag IB 2025'"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
          />
          <Button
            onClick={() => {
              if (!internalReason) { setError("Kies eerst een reden."); return; }
              apply("BOOK_INTERNAL", undefined, { reason: internalReason, note: internalNote.trim() || undefined });
            }}
            loading={busy}
          >
            <BookCheck size={18} /> Boeken zonder koppeling
          </Button>
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2"><X size={16} /> {error}</p>}
      </div>
    </Modal>
  );
}